import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Button,
  Chip,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { getPageContents, deletePageContent } from '../services/pageContentService';
import DashboardLayout from '../components/DashboardLayout';
import AdminEmptyState from '../components/AdminEmptyState';
import AdminPageHeader from '../components/AdminPageHeader';
import AdminPagination from '../components/AdminPagination';
import AdminSection from '../components/AdminSection';
import useAdminPagination from '../hooks/useAdminPagination';

function PageContentList() {
  const [pageContents, setPageContents] = useState([]);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const {
    page,
    rowsPerPage,
    count,
    paginatedItems: paginatedPageContents,
    handlePageChange,
    handleRowsPerPageChange,
  } = useAdminPagination(pageContents, 10);

  useEffect(() => {
    fetchPageContents();
  }, []);

  const fetchPageContents = async () => {
    try {
      const data = await getPageContents();
      setPageContents(Array.isArray(data) ? data : []);
      setError('');
    } catch (loadError) {
      console.error('Error fetching page contents:', loadError);
      setPageContents([]);
      setError('Failed to load page contents.');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this page content?')) {
      return;
    }

    try {
      await deletePageContent(id);
      fetchPageContents();
    } catch (deleteError) {
      console.error('Error deleting page content:', deleteError);
      setError('Failed to delete page content.');
    }
  };

  return (
    <DashboardLayout>
      <Stack spacing={3}>
        <AdminPageHeader
          eyebrow="Content"
          title="Page Content"
          description="Manage section-level copy, metadata, and supporting assets for static pages."
          meta={<Chip label={`${pageContents.length} sections`} variant="outlined" />}
          action={
            <Button variant="contained" onClick={() => navigate('/page-contents/create')}>
              Create Page Content
            </Button>
          }
        />

        {error ? <Alert severity="error">{error}</Alert> : null}

        <AdminSection sx={{ p: 0, overflow: 'hidden' }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Section Name</TableCell>
                <TableCell>Meta Title</TableCell>
                <TableCell>Meta Description</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pageContents.length ? (
                paginatedPageContents.map((content) => (
                  <TableRow key={content.id}>
                    <TableCell sx={{ fontWeight: 600 }}>{content.sectionName}</TableCell>
                    <TableCell>{content.metaTitle || '-'}</TableCell>
                    <TableCell sx={{ maxWidth: 360 }}>
                      <Typography noWrap color="text.secondary">
                        {content.metaDescription || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton color="primary" onClick={() => navigate(`/page-contents/edit/${content.id}`)}>
                        <EditIcon />
                      </IconButton>
                      <IconButton color="error" onClick={() => handleDelete(content.id)}>
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={4}>
                    <AdminEmptyState
                      title="No page content sections yet"
                      description="Create section content to manage static page areas from the admin."
                    />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <AdminPagination
            count={count}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
          />
        </AdminSection>
      </Stack>
    </DashboardLayout>
  );
}

export default PageContentList;
